import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { FC, PropsWithChildren, useContext, useState, createContext, useMemo } from 'react';

import { useAuth } from 'hooks/useAuth';

import { AuditionUserRegistration } from './u002/AuditionUserRegistration';
import { AuditionForm } from './u002/AuditionForm';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { PATHS } from 'commons/paths';
import { useGetAudition, GetAuditionResponse } from 'hooks/u002/useGetAuditionHooks';
import LoadingModal from 'organisms/LoadingModal';
import AuditionFormCompleteModal from 'organisms/u002/AuditionFormCompleteModal';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AuditionFormConfirm } from './u002/AuditionFormConfirm';
import { ApplyForm } from 'api/U002';
import { AppContext } from 'templates/AppContextProvider';
import { UserDetailCreate } from './u002/UserDetailCreate';
import { SpecificUseConsent } from './u002/SpecificUseConsent';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { UnderEighteenGuidance } from './u002/UnderEighteenGuidance';
import { isUnderEighteen } from 'domains/User';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { PrivacyPolicyConsent } from './u002/PrivacyPolicyConsent';
import { AuditionTermsConsent } from './u002/AuditionTermsConsent';
import { decryptExamNo } from 'commons/auditionForm';
import { Questionnaire } from './u002/Questionnaire';
import { AuditionFormValues } from 'commons/auditionForm/types';
import { NotFound } from './404';
import { GroupConfirm } from './u002/GroupConfirm';
import { AuditionFormSample } from './u002/AuditionFormSample';
import { AuditionFormValuesSchemaInput } from 'hooks/u002/useAuditionForm';

/**
 * 応募フォーム構造
 * エントリポイント:<AuditionFormTop>
 *  ├ 終了済み
 *  ├ 開始前
 *  └ 募集中:<RecruitingAudition>
 *    ├ ログイン済み
 *    │ ├ 応募フォーム:<AuditionForm>
 *    │ ├ 応募フォーム確認:<AuditionFormConfirm>
 *    │ └ 応募完了モーダル:<AuditionFormCompleteModal>
 *    └ 未ログイン:<AuditionUserRegistration>
 *      ├ アカウント確認画面:<Top>
 *      ├ 利用規約画面:<TermsOfServiceConsent>
 *      ├ プライバシーポリシー画面:<PrivacyPolicyConsent>
 *      ├ 特定利用についての画面:<SpecificUse>
 *      ├ アカウント作成画面:<UserRegistration>
 *      └ アカウント作成MFA確認画面:<UserRegistrationMfa>
 */

type Audition = GetAuditionResponse;

// AuditionFormTopの画面表示用テンプレート
export const AuditionTopPage: FC<PropsWithChildren<{ title: string }>> = (props) => (
  <>
    <AppLogoHeader />
    <PageContent>
      <h1 className="bg-[--c-background] text-xl mb-16 text-center">{props.title}</h1>
      {props.children}
    </PageContent>
    <UnauthAppFooter />
  </>
);

// 募集中のオーディションの応募フォーム
export type RecruitingAuditionProps = {
  audition: Audition;
  userDetailFlg: string;
  birth: Date;
  phoneNumber: string;
  specificUseConsent: string;
  privacyPolicyConsent: string;
  auditionTermsConsent: string;
  groupConfirm: string;
  isSample: boolean;
};
type PageState =
  | { page: 'SpecificUseConsent' }
  | { page: 'PrivacyPolicyConsent' }
  | { page: 'AuditionTermsConsent' }
  | { page: 'AuditionForm'; values?: AuditionFormValuesSchemaInput }
  | {
      page: 'AuditionFormConfirm';
      applyForm: ApplyForm[];
      values: AuditionFormValues;
      rawValues: AuditionFormValuesSchemaInput;
    }
  | { page: 'UserDetailCreate' }
  | { page: 'GroupConfirm' };

const RecruitingAudition: FC<RecruitingAuditionProps> = ({
  audition,
  userDetailFlg,
  birth,
  phoneNumber,
  specificUseConsent,
  privacyPolicyConsent,
  auditionTermsConsent,
  groupConfirm,
  isSample,
}) => {
  const { audition_id, examination_no } = useContext(AuditionFormRouteContext);
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const { isLoading, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { isGlobal } = useContext(AppContext);
  const [params] = useSearchParams();
  const representative = params.get('representative');

  // 初期ページ状態の設定
  const [pageState, setPageState] = useState<PageState>(() => {
    if (specificUseConsent === '1') return { page: 'SpecificUseConsent' };
    if (privacyPolicyConsent === '1') return { page: 'PrivacyPolicyConsent' };
    if (auditionTermsConsent === '1') return { page: 'AuditionTermsConsent' };
    if (userDetailFlg === '1') return { page: 'UserDetailCreate' };
    if (groupConfirm === '1' || groupConfirm === '2') return { page: 'GroupConfirm' };
    return { page: 'AuditionForm' };
  });

  console.log('pageState:', pageState);

  if (isLoading) return <LoadingModal />;

  // 未認証の場合はアカウント確認画面、グループ応募の場合は生年月確認画面へ
  if (!isAuthenticated || representative) {
    return <AuditionUserRegistration audition={{ ...audition, audition_id }} />;
  }

  // 18歳未満のグローバルユーザーの場合
  if (isGlobal && specificUseConsent === '1' && isUnderEighteen(birth, new Date())) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: 'head',
        }}
      >
        <UnderEighteenGuidance auditionId={audition_id} birth={birth} />
      </GoogleReCaptchaProvider>
    );
  }

  if (isSample) {
    return <AuditionFormSample />;
  }

  return (
    <>
      {/* 特定利用同意画面 */}
      {pageState.page === 'SpecificUseConsent' && (
        <SpecificUseConsent
          audition={{ ...audition, audition_id }}
          onNext={() => {
            if (privacyPolicyConsent === '1') {
              setPageState({ page: 'PrivacyPolicyConsent' });
            } else if (auditionTermsConsent === '1') {
              setPageState({ page: 'AuditionTermsConsent' });
            } else if (userDetailFlg === '1') {
              setPageState({ page: 'UserDetailCreate' });
            } else if (groupConfirm === '1') {
              setPageState({ page: 'GroupConfirm' });
            } else {
              setPageState({ page: 'AuditionForm' });
            }
          }}
        />
      )}

      {/* プライバシーポリシー同意画面 */}
      {pageState.page === 'PrivacyPolicyConsent' && (
        <PrivacyPolicyConsent
          audition={{ ...audition, audition_id }}
          onNext={() => {
            if (auditionTermsConsent === '1') {
              setPageState({ page: 'AuditionTermsConsent' });
            } else if (userDetailFlg === '1') {
              setPageState({ page: 'UserDetailCreate' });
            } else if (groupConfirm === '1' || groupConfirm === '2') {
              setPageState({ page: 'GroupConfirm' });
            } else {
              setPageState({ page: 'AuditionForm' });
            }
          }}
        />
      )}

      {/* オーディション利用規約同意画面 */}
      {pageState.page === 'AuditionTermsConsent' && (
        <AuditionTermsConsent
          audition={{ ...audition, audition_id }}
          onNext={() => {
            if (userDetailFlg === '1') {
              setPageState({ page: 'UserDetailCreate' });
            } else if (groupConfirm === '1' || groupConfirm === '2') {
              setPageState({ page: 'GroupConfirm' });
            } else {
              setPageState({ page: 'AuditionForm' });
            }
          }}
        />
      )}

      {/* ユーザー詳細作成画面 */}
      {pageState.page === 'UserDetailCreate' && (
        <UserDetailCreate
          title={audition.title}
          audition_id={audition_id}
          examination_no={examination_no}
          birth={birth}
          phoneNumber={phoneNumber}
          onNext={() => {
            if (groupConfirm === '1' || groupConfirm === '2') {
              setPageState({ page: 'GroupConfirm' });
            } else {
              setPageState({ page: 'AuditionForm' });
            }
          }}
          now_dtm={audition.now_dtm}
        />
      )}

      {/* グループ応募確認画面 */}
      {pageState.page === 'GroupConfirm' && (
        <GroupConfirm
          audition={{ ...audition, audition_id }}
          onNext={() => setPageState({ page: 'AuditionForm' })}
        />
      )}

      {/* 応募フォーム */}
      {pageState.page === 'AuditionForm' && (
        <AuditionForm
          title={audition.title}
          audition_id={audition_id}
          examination_no={examination_no}
          values={pageState.values}
          onNext={(param) =>
            setPageState({
              page: 'AuditionFormConfirm',
              applyForm: param.applyForm,
              values: param.values,
              rawValues: param.rawValues,
            })
          }
        />
      )}

      {/* 応募フォーム確認画面 */}
      {pageState.page === 'AuditionFormConfirm' && (
        <AuditionFormConfirm
          title={audition.title}
          audition_id={audition_id}
          examination_no={examination_no}
          applyForm={pageState.applyForm}
          values={pageState.values}
          onSuccess={() => setIsOpen(true)}
          onBack={() => setPageState({ page: 'AuditionForm', values: pageState.rawValues })}
        />
      )}

      {/* 完了モーダル */}
      {isOpen && (
        <AuditionFormCompleteModal
          end_custom_txt={audition.end_custom_txt}
          domain_txt={audition.domain_txt}
          onClose={() => {
            setIsOpen(false);
            nav(PATHS.MYPAGE);
          }}
          closeLabel={tAuditionForm('TU002_039')}
        />
      )}
    </>
  );
};

type AuditionFormRouteContextValue = {
  group: string;
  audition_id: string;
  examination_no: number;
  isSample: boolean;
};
export const AuditionFormRouteContext = createContext<AuditionFormRouteContextValue>({
  group: '',
  audition_id: '',
  examination_no: 0,
  isSample: false,
});
// ルートパラメータのバリデーション
const AuditionFormRouteValidation = ({ children }: PropsWithChildren) => {
  const routeParams = useParams();
  console.log(routeParams);
  const { group, audition_id, examination_no: _examination_no } = routeParams;

  // パスパラメータのバリデーション
  if (!group || !audition_id || !_examination_no) {
    throw new Error('audition_id and examination_no is required');
  }

  const { examination_no, isSample } = useMemo(() => {
    let examination_no: number = NaN;
    let isSample = false;
    try {
      const result = decryptExamNo(audition_id, _examination_no);
      examination_no = result.examNo;
      isSample = result.isSample;
      console.debug('decrypted examination_no:', examination_no, 'isSample:', isSample);
    } catch (e) {
      console.error(e);
      examination_no = NaN;
    }
    return {
      examination_no,
      isSample,
    };
  }, [audition_id, _examination_no]);

  if (isNaN(examination_no)) {
    return <NotFound />;
  }

  return (
    <AuditionFormRouteContext.Provider
      value={{
        group,
        audition_id,
        examination_no,
        isSample,
      }}
    >
      {children}
    </AuditionFormRouteContext.Provider>
  );
};

export const GetAuditionContext = createContext<GetAuditionResponse>({
  examination_no: 0,
  title: '',
  domain_txt: '',
  end_custom_txt: '',
  start_txt: '',
  end_txt: '',
  user_detail_flg: '',
  phone_number: '',
  start_dtm: new Date(),
  end_dtm: new Date(),
  birth: new Date(),
  specific_use_consent: '',
  privacy_policy_consent: '',
  audition_terms_consent: '',
  group_ap: '',
  googletag: '',
  googletag2: '',
  now_dtm: new Date(),
});

// 応募フォームの有効性を検証する
export const AuditionFormValid = () => {
  const { currentLang } = useContext(AppContext);
  const { audition_id, examination_no, isSample } = useContext(AuditionFormRouteContext);
  const [searchParam] = useSearchParams();
  const representative = searchParam.get('representative');
  const [data, isDataLoading, err] = useGetAudition({
    audition_id,
    examination_no,
    lang: currentLang,
    representative_user: representative ?? undefined,
  });
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');

  // ログイン可否のローディング
  if (isDataLoading) {
    return <LoadingModal />;
  }
  if (err) {
    let errorMessage = tAuditionForm('TU002_050');
    if (err.message === 'MU002F0115E') {
      errorMessage = tAuditionForm('TU002_062');
    }
    if (err.message === 'MU002F0125E') {
      errorMessage = tAuditionForm('TU002_079');
    }
    if (err.message === 'MU002A0103E') {
      errorMessage = tAuditionForm('TU002_063');
    }
    return (
      <AuditionTopPage title={tAuditionForm('TU002_050')}>
        <p className="text-[--c-error]">{errorMessage}</p>
      </AuditionTopPage>
    );
  }
  if (data == null) {
    return <></>;
  }
  if (!isSample) {
    // 2.取得できた審査Noが2以上の場合、アンケートのためAPIから応募者情報を取得し、応募フォーム表示に遷移する。
    if (data.examination_no >= 2) {
      return (
        <GetAuditionContext.Provider value={data}>
          <Questionnaire />
        </GetAuditionContext.Provider>
      );
    }

    const now = data.now_dtm;
    // 募集前の場合
    if (data.start_dtm > now) {
      return (
        <AuditionTopPage title={data.title}>
          <p className="whitespace-pre-wrap">{data.start_txt}</p>
        </AuditionTopPage>
      );
    }

    // 募集終了後の場合
    if (data.end_dtm < now) {
      return (
        <AuditionTopPage title={data.title}>
          <p className="whitespace-pre-wrap">{data.end_txt}</p>
        </AuditionTopPage>
      );
    }

    // 現在審査Noが違う場合
    if (data.examination_no != null && data.examination_no !== examination_no) {
      return (
        <AuditionTopPage title={data.title}>
          <p className="whitespace-pre-wrap">{data.end_txt}</p>
        </AuditionTopPage>
      );
    }
  }

  return (
    <GetAuditionContext.Provider value={data}>
      <RecruitingAudition
        audition={data}
        userDetailFlg={data.user_detail_flg}
        birth={data.birth}
        phoneNumber={data.phone_number}
        specificUseConsent={data.specific_use_consent}
        privacyPolicyConsent={data.privacy_policy_consent}
        auditionTermsConsent={data.audition_terms_consent}
        groupConfirm={data.group_ap}
        isSample={isSample}
      />
    </GetAuditionContext.Provider>
  );
};

// 応募フォームのエントリポイント
export const AuditionFormTop = () => (
  <AuditionFormRouteValidation>
    <AuditionFormRouteContext.Consumer>
      {() => <AuditionFormValid />}
    </AuditionFormRouteContext.Consumer>
  </AuditionFormRouteValidation>
);
